/**
 * Call-progress tones for Kai Voice ("Marimba"), synthesised with Web Audio
 * (no asset to load, works offline). Everything is percussive — a short
 * wooden tap with an exponential decay — so nothing sustains into the
 * greeting audio. All of it sits ~20dB under the voice level: reassurance,
 * not an alert.
 *
 * - connect pulse: one tap every 1.8s while the WebRTC session is being set
 *   up. Starts only after a short delay so a fast connect stays silent.
 * - connected: two quick rising taps when the session comes up — only if the
 *   pulse had started, so the transition reads as intentional.
 * - end: one lower tap on hang-up.
 */
const CONNECT_START_DELAY_MS = 700;
const CONNECT_PULSE_INTERVAL_MS = 1800;
const GAIN = 0.055;

let audioContext: AudioContext | null = null;
let connectStartTimer: ReturnType<typeof setTimeout> | null = null;
let connectPulseTimer: ReturnType<typeof setInterval> | null = null;
let connectPulseStarted = false;

/** The one AudioContext for call sounds (tones, hold music). Created inside the call-start click so Safari lets it play. */
export const getCallAudioContext = (): AudioContext | null => {
  try {
    const Ctor = (window as any).AudioContext ?? (window as any).webkitAudioContext;
    if (!Ctor) return null;
    if (!audioContext) audioContext = new Ctor();
    // iOS Safari reports "interrupted" once the microphone takes the audio session.
    if (audioContext && audioContext.state !== "running" && audioContext.state !== "closed") {
      audioContext.resume().catch(() => {});
    }
    return audioContext;
  } catch (_) {
    return null;
  }
};

/** A wooden tap: sine fundamental with a fast exponential decay plus a faint, shorter 3rd harmonic. */
const tap = (ctx: AudioContext, frequency: number, at: number, durationSec: number, gain = GAIN) => {
  const osc = ctx.createOscillator();
  const env = ctx.createGain();
  osc.type = "sine";
  osc.frequency.value = frequency;
  env.gain.setValueAtTime(gain, at);
  env.gain.exponentialRampToValueAtTime(0.0005, at + durationSec);
  osc.connect(env);
  env.connect(ctx.destination);
  osc.start(at);
  osc.stop(at + durationSec + 0.02);

  const harmonic = ctx.createOscillator();
  const harmonicEnv = ctx.createGain();
  harmonic.type = "sine";
  harmonic.frequency.value = frequency * 3;
  harmonicEnv.gain.setValueAtTime(gain * 0.25, at);
  harmonicEnv.gain.exponentialRampToValueAtTime(0.0005, at + durationSec * 0.4);
  harmonic.connect(harmonicEnv);
  harmonicEnv.connect(ctx.destination);
  harmonic.start(at);
  harmonic.stop(at + durationSec);
};

const playConnectPulse = () => {
  const ctx = getCallAudioContext();
  if (!ctx) return;
  tap(ctx, 440, ctx.currentTime, 0.18);
};

const clearConnectTimers = () => {
  if (connectStartTimer) {
    clearTimeout(connectStartTimer);
    connectStartTimer = null;
  }
  if (connectPulseTimer) {
    clearInterval(connectPulseTimer);
    connectPulseTimer = null;
  }
};

export const startConnectTone = (): void => {
  clearConnectTimers();
  connectPulseStarted = false;
  connectStartTimer = setTimeout(() => {
    connectStartTimer = null;
    connectPulseStarted = true;
    playConnectPulse();
    connectPulseTimer = setInterval(playConnectPulse, CONNECT_PULSE_INTERVAL_MS);
  }, CONNECT_START_DELAY_MS);
};

/** Stop the pulse; a successful connect gets the rising double tap (only if the pulse had played). */
export const stopConnectTone = (connected: boolean): void => {
  const hadStarted = connectPulseStarted;
  clearConnectTimers();
  connectPulseStarted = false;
  if (connected && hadStarted) {
    const ctx = getCallAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;
    tap(ctx, 440, now, 0.16, GAIN * 0.9);
    tap(ctx, 659.3, now + 0.12, 0.22, GAIN * 0.9);
  }
};

export const playEndTone = (): void => {
  clearConnectTimers();
  connectPulseStarted = false;
  const ctx = getCallAudioContext();
  if (!ctx) return;
  tap(ctx, 293.7, ctx.currentTime, 0.3);
};
